import {
  hasActionPermission,
  hasInputPermission,
} from '@tribeplatform/gql-client/lib'
import { Network } from '@tribeplatform/gql-client/types'
import { PlanBadge } from '@tribeplatform/react-components/common/components'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useNetwork, useUpdateNetwork } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { ReferralSettings } from './ReferralSettings.js'

type FormValues = Pick<
  Network,
  'tribeBranding' | 'referralProgramEnabled' | 'referralLink'
>

export const WhiteLabelSettings = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: updateNetwork, isLoading } = useUpdateNetwork()

  const { actionPermission } = hasActionPermission(
    network?.authMemberProps?.permissions ?? [],
    'updateNetwork',
  )
  const { authorized } = hasInputPermission(
    actionPermission.inputPermissions,
    'input.tribeBranding',
  )

  const onSubmit = async (data: FormValues) => {
    const { tribeBranding, referralProgramEnabled, referralLink } = data
    try {
      await updateNetwork({
        input: { tribeBranding, referralProgramEnabled, referralLink },
      })
      toast({
        title: $t({
          defaultMessage: 'Branding & Referrals settings updated',
          id: 'Admin.WhiteLabelSettings.Updated',
        }),
        status: 'success',
      })
    } catch (e) {
      toast({
        title: $t({
          defaultMessage: 'Unable to update Branding & Referrals settings',
          id: 'Admin.WhiteLabelSettings.FailedToUpdate',
        }),
        status: 'error',
      })
    }
  }

  return (
    <Card>
      <Card.Header
        title={
          <div className="flex items-center space-s-2">
            <div>
              <T
                defaultMessage="Branding & Referrals"
                id="Admin.WhiteLabelSettings"
              />
            </div>
            <div>
              <PlanBadge authorized={authorized} />
            </div>
          </div>
        }
      />
      <Card.Content>
        <Form defaultValues={network} onSubmit={onSubmit}>
          <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
            <Form.Toggle
              name="tribeBranding"
              label={$t({
                defaultMessage: 'Display {BRAND} badge',
                id: 'Admin.WhiteLabelSettings.HidePlatform',
              })}
              helperText={$t({
                defaultMessage:
                  'Show a “Made in {BRAND}” badge on your site and in email footers.',
                id: 'Admin.WhiteLabelSettings.HidePlatform.Description',
              })}
              disabled={!authorized}
            />
            <ReferralSettings disabled={!authorized} />
            <Form.Actions>
              <Button type="submit" loading={isLoading} disabled={!authorized}>
                <T defaultMessage="Update" id="Generics.Update" />
              </Button>
            </Form.Actions>
          </div>
        </Form>
      </Card.Content>
    </Card>
  )
}
